export type SoloMode = 'laugh' | 'blink';
/** Ce qui a un record : les deux modes du miroir (temps tenu) et la série de smileys (score). */
export type RecordKey = SoloMode | 'smiley';
export const RECORD_KEYS: readonly RecordKey[] = ['laugh', 'blink', 'smiley'];

/** Sous-ensemble de `Storage` (localStorage), injectable pour les tests. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface RecordBook {
  best(mode: RecordKey): number | null;
  /** `value` : temps tenu (ms) ou score ; plus haut = mieux. */
  submit(mode: RecordKey, value: number): { best: number | null; isRecord: boolean };
}

/** v2 : miroir accéléré et grimaces (issue #23), les temps d'avant ne sont plus comparables. */
const KEY = 'barbichette.records.v2';
const MAX_MS = 24 * 3600 * 1000;
const valid = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= MAX_MS;

/**
 * Meilleurs temps du solo, gardés dans CE navigateur uniquement (jamais envoyés).
 * Stockage indisponible, qui lève ou corrompu : le jeu continue, records en mémoire.
 */
export function createRecordBook(store: KeyValueStore | null): RecordBook {
  const records: Partial<Record<RecordKey, number>> = {};
  try {
    const parsed: unknown = JSON.parse(store?.getItem(KEY) ?? '{}');
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      for (const mode of RECORD_KEYS) {
        const v = (parsed as Record<string, unknown>)[mode];
        if (valid(v)) records[mode] = v;
      }
    }
  } catch {
    // Illisible : on repart de zéro.
  }

  return {
    best: (mode) => records[mode] ?? null,
    submit(mode, heldMs) {
      const previous = records[mode] ?? null;
      if (!valid(heldMs)) return { best: previous, isRecord: false };
      if (previous !== null && heldMs <= previous) return { best: previous, isRecord: false };
      records[mode] = heldMs;
      try {
        store?.setItem(KEY, JSON.stringify(records));
      } catch {
        // Quota, navigation privée… : record gardé pour la session.
      }
      return { best: heldMs, isRecord: true };
    },
  };
}
